import React, { useEffect, useRef, useState } from "react";
import * as echarts from "echarts/core";

import {
  CustomChart,
  LineChart,
  LineSeriesOption,
  CustomSeriesOption,
} from "echarts/charts";

import {
  GridComponent,
  GridComponentOption,
  TooltipComponent,
  TooltipComponentOption,
  ToolboxComponent,
  ToolboxComponentOption,
} from "echarts/components";

import { CanvasRenderer, SVGRenderer } from "echarts/renderers";

echarts.use([
  LineChart,
  CustomChart,
  GridComponent,
  TooltipComponent,
  ToolboxComponent,
  CanvasRenderer,
  SVGRenderer,
]);

export type EChartsOption = echarts.ComposeOption<
  | LineSeriesOption
  | GridComponentOption
  | CustomSeriesOption
  | TooltipComponentOption
  | ToolboxComponentOption
>;

export type RenderItem = CustomSeriesOption["renderItem"];

interface ChartProp {
  option: EChartsOption;
  notMerge?: boolean;
  setOptionMode?: "merge" | "replace" | "clear-then-set";
  style?: Object;
  className?: string;
  onChange?: (instance: echarts.EChartsType) => void;
  handleChartClick?: any;
  renderer?: "canvas" | "svg";
}

const Chart = ({
  option,
  notMerge = false,
  setOptionMode = "merge",
  style,
  className,
  onChange,
  handleChartClick,
  renderer = "canvas"
}: ChartProp) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [chartInstance, setChartInstance] = useState<echarts.EChartsType>();

  useEffect(() => {
    if (chartRef.current) {
      setChartInstance(echarts.init(chartRef.current, undefined, { renderer }));
    }

    return () => {
      chartInstance?.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderer]);

  useEffect(() => {
    if (chartInstance && option) {
      if (setOptionMode === "clear-then-set") {
        chartInstance?.clear();
        chartInstance?.setOption(option, false);
      } else if (setOptionMode === "replace") {
        chartInstance?.setOption(option, true);
      } else {
        chartInstance?.setOption(option, notMerge);
      }

      if (onChange && !chartInstance.isDisposed()) {
        onChange(chartInstance);
      }

      if (handleChartClick) {
        const onClick = (params: any) => {
          handleChartClick && handleChartClick(params, chartInstance);
        }

        chartInstance?.getZr().off('click');
        chartInstance?.getZr().on('click', onClick);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartInstance, option, notMerge, setOptionMode]);

  return (
    <div
      ref={chartRef}
      className={className}
      style={style}
    />
  );
};

export default Chart;
